import { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link, LinkProps } from '@tanstack/react-router';
import {
  InfoCard,
  InfoCardBody,
  InfoCardHeader,
} from '@/components/ui/InfoCard.tsx';
import Dot from '@/components/ui/Dot.tsx';
import Chip from '@/components/ui/Chip.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import { STATUS_TONE_COLORS, StatusTone } from '@/components/ui/statusTone.ts';
import { cn } from '@/lib/utils.ts';

export interface AttentionPanelItem {
  id: string;
  /** Co się dzieje: „Zaległe płatności”, „Wariant bez zapisów”. */
  label: string;
  /** Czego dokładnie dotyczy — nazwiska, terminy, liczby. */
  detail: ReactNode;
  /** Skrót liczbowy po prawej: kwota, licznik. Bez niego pigułka znika. */
  badge?: string | null;
  tone: StatusTone;
  /** Dokąd prowadzi wiersz. Cel podaje ekran literałem trasy, nie adresem. */
  link?: Pick<LinkProps, 'to' | 'params' | 'search'>;
  /** Napis odsyłacza; bez niego wiersz prowadzi pod hasłem „Otwórz”. */
  actionLabel?: string;
  /** Linia audytu pod zdaniem — kto i kiedy odłożył sprawę. */
  note?: string;
  /** Menu wiersza; rysowane obok odsyłacza, poza nim, bo przycisk w środku `<a>` to nieprawidłowy HTML. */
  actions?: ReactNode;
}

interface AttentionPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  /** Dopisek w linii tytułu — na kiedy stan jest aktualny. */
  meta?: ReactNode;
  items: AttentionPanelItem[];
  emptyDescription?: string;
  /** Przełącznik obok pigułki — np. „3 odłożone”. */
  headerAction?: ReactNode;
}

/** Pigułka licznika bierze ton najcięższej pozycji — panel ma krzyczeć tak głośno, jak jego najgorszy wiersz. */
const worstTone = (items: AttentionPanelItem[]): StatusTone => {
  if (items.some((item) => item.tone === 'error')) return 'error';
  if (items.some((item) => item.tone === 'caution')) return 'caution';
  if (items.some((item) => item.tone === 'warning')) return 'warning';
  return 'neutral';
};

const TONE_TO_CHIP = {
  neutral: 'neutral',
  info: 'info',
  success: 'success',
  warning: 'warning',
  caution: 'warning',
  error: 'error',
} as const;

/**
 * Lista rzeczy, którymi trzeba się zająć — zaległe raty, niepodpisane zgody,
 * wariant bez zapisów. Każdy wiersz prowadzi tam, gdzie da się to załatwić.
 *
 * Panel nie liczy niczego sam: pozycje przychodzą gotowe, razem z tonem
 * i celem, bo tylko ekran wie, co w jego danych wymaga reakcji.
 */
const AttentionPanel = ({
  title,
  meta,
  items,
  emptyDescription = 'Nic nie wymaga teraz reakcji.',
  headerAction,
  className,
  ...props
}: AttentionPanelProps) => (
  <InfoCard className={className} {...props}>
    <InfoCardHeader
      title={title}
      aside={
        (items.length > 0 || headerAction) && (
          <span className="flex items-center gap-2">
            {items.length > 0 && (
              <Chip
                label={String(items.length)}
                tone={TONE_TO_CHIP[worstTone(items)]}
                fill="soft"
                size="sm"
              />
            )}
            {headerAction}
          </span>
        )
      }
    >
      {meta && (
        <span className="mt-1 block text-[13px] text-neutral-400">{meta}</span>
      )}
    </InfoCardHeader>

    {items.length === 0 ? (
      <InfoCardBody>
        <NoItems title="Czysto" description={emptyDescription} />
      </InfoCardBody>
    ) : (
      <div className="flex flex-col">
        {items.map((item, index) => {
          const row = (
            <>
              <Dot
                size="md"
                className="shrink-0"
                style={{ backgroundColor: STATUS_TONE_COLORS[item.tone].dot }}
              />
              <span className="min-w-0">
                <span className="block text-[15px] font-semibold text-ink">
                  {item.label}
                </span>
                <span className="mt-0.5 block text-[13px] text-neutral-500">
                  {item.detail}
                </span>
                {item.note && (
                  <span className="mt-0.5 block text-[12px] text-neutral-400">
                    {item.note}
                  </span>
                )}
              </span>
              {item.badge && (
                <Chip
                  label={item.badge}
                  tone={TONE_TO_CHIP[item.tone]}
                  fill="soft"
                  // Kwota nie może się urwać na wąskim ekranie — ściska się kolumna opisu.
                  className="shrink-0"
                />
              )}
              {item.link && (
                <span className="ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-semibold text-info-300">
                  {item.actionLabel ?? 'Otwórz'}
                  <ArrowRight className="size-3.5" aria-hidden />
                </span>
              )}
            </>
          );

          const rowClass = 'flex min-w-0 flex-1 items-center gap-3 px-5 py-3.5';

          return (
            <div
              key={item.id}
              className={cn(
                'flex items-center',
                index > 0 && 'border-t border-neutral-100',
              )}
            >
              {item.link ? (
                <Link
                  to={item.link.to}
                  params={item.link.params}
                  search={item.link.search}
                  className={cn(rowClass, 'no-underline hover:bg-neutral-50')}
                >
                  {row}
                </Link>
              ) : (
                <div className={rowClass}>{row}</div>
              )}
              {item.actions && (
                <div className="shrink-0 pr-3">{item.actions}</div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </InfoCard>
);

export default AttentionPanel;
